import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import Loader from "react-loader-spinner";

import NavBar from "../../components/fragments/NavBar";
import Footer from "../../components/fragments/Footer";
import { Container } from "../../components/styles/Layout";
import { Button } from "../../components/styles/Button";
import { LoaderWrapper } from "../../components/styles/Loader";
import { size } from "../../components/styles/Breakpoints";
import NoPhoto from "../../assets/img/no-images.jpg";

import useSeo from "../../hooks/useSeo";
import useJsonLd from "../../hooks/useJsonLd";
import { localitySeo, formatPrice, propertyAltBase } from "../../data/seo";
import { localitySchema } from "../../data/structuredData";
import { propertyPath } from "../../data/propertySlug";
import { AREAS, findArea, areaStats } from "../../data/areaPages";
import NotFound from "./NotFound";

const Header = styled.div`
    background: ${(props) => props.theme.darkgradient};
    color: white;
    padding: 3rem 0;
    margin-bottom: 2.5rem;
`;

const Title = styled.h1`
    margin: 0 0 0.75rem 0;
    color: white;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: 1px;
`;

const Lede = styled.p`
    margin: 0;
    max-width: 46rem;
    color: white;
    font-size: 1rem;
    line-height: 1.6;
`;

const LocalCopy = styled.p`
    max-width: 46rem;
    margin: 0;
`;

/*
 * Only Wimbledon carries landmarkImage today - areas.json leaves the other
 * thirteen without one rather than reaching for a generic stock photo of
 * each. Text and image sit side by side above size.md; below it the photo
 * would otherwise be squeezed into a narrow column, so it drops beneath the
 * text instead.
 */
const IntroRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 2rem;
    margin: 0 0 2rem 0;
    @media screen and (max-width: ${size.md}px) {
        flex-direction: column;
        gap: 1rem;
    }
`;

const LandmarkFigure = styled.figure`
    flex: none;
    width: 20rem;
    margin: 0;
    @media screen and (max-width: ${size.md}px) {
        width: 100%;
    }
`;

const LandmarkImage = styled.img`
    display: block;
    width: 100%;
    height: 14rem;
    object-fit: cover;
    @media screen and (max-width: ${size.md}px) {
        height: 16rem;
    }
`;

const LandmarkCredit = styled.figcaption`
    font-size: 0.7rem;
    color: ${(props) => props.theme.grey};
    margin-top: 0.4rem;
`;

const StatRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1px;
    background: ${(props) => props.theme.lightgrey};
    border: 1px solid ${(props) => props.theme.lightgrey};
    margin: 0 0 2.5rem 0;
    @media screen and (max-width: ${size.sm}px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const Stat = styled.div`
    background: white;
    padding: 1rem;
`;

const StatValue = styled.div`
    font-size: 1.6rem;
    color: ${(props) => props.theme.darkblue};
    line-height: 1.1;
`;

const StatLabel = styled.div`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${(props) => props.theme.grey};
    margin-top: 0.3rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.35rem;
    font-weight: 400;
    line-height: normal;
    color: ${(props) => props.theme.darkblue};
    border-bottom: 1px solid ${(props) => props.theme.lightgrey};
    padding-bottom: 0.5rem;
    margin: 2.5rem 0 1.25rem 0;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    @media screen and (max-width: ${size.md}px) {
        grid-template-columns: repeat(2, 1fr);
    }
    @media screen and (max-width: ${size.sm}px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled(Link)`
    display: block;
    border: 1px solid ${(props) => props.theme.lightgrey};
    transition: transform 0.25s ease;
    &:hover {
        transform: translate(0, -3px);
    }
`;

/*
 * A real <img>, not a background-image div. Background images cannot carry
 * alt text and are not indexed by Google Images, so a card full of property
 * photography was invisible to both screen readers and image search.
 * object-fit reproduces what background-size: cover was doing.
 */
const CardImage = styled.img`
    display: block;
    width: 100%;
    height: 11rem;
    object-fit: cover;
    object-position: center;
`;

const CardBody = styled.div`
    padding: 0.85rem 1rem 1rem 1rem;
`;

const CardAddress = styled.h3`
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
    font-weight: 500;
    color: ${(props) => props.theme.black};
`;

const CardMeta = styled.div`
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
`;

const CardPrice = styled.div`
    margin-top: 0.4rem;
    color: ${(props) => props.theme.darkblue};
    font-weight: 500;
`;

const Status = styled.span`
    display: inline-block;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 0.15rem 0.45rem;
    margin-bottom: 0.5rem;
    color: white;
    background: ${(props) =>
        props.status === "Available"
            ? props.theme.green
            : props.status === "Under Offer"
            ? props.theme.orange
            : props.theme.grey};
`;

const GuideBox = styled.div`
    background: #f3fbff;
    padding: 1.5rem;
    margin: 2.5rem 0;
`;

const Cta = styled.div`
    text-align: center;
    margin: 3rem 0;
`;

const AreaLinks = styled.div`
    border-top: 1px solid ${(props) => props.theme.lightgrey};
    padding-top: 1.5rem;
    margin: 3rem 0 2rem 0;
    font-size: 0.9rem;
    & a {
        display: inline-block;
        margin: 0 1rem 0.5rem 0;
        color: ${(props) => props.theme.darkblue};
    }
    & a:hover {
        text-decoration: underline;
    }
`;

function PropertyCard({ property }) {
    const photo = property.photos && property.photos.length ? property.photos[0] : NoPhoto;
    const beds = property.bedrooms ? property.bedrooms + " bedroom " : "";
    const isLetting = property.category !== "Sales";

    return (
        <Card to={propertyPath(property)}>
            <CardImage src={photo} alt={propertyAltBase(property)} loading="lazy" />
            <CardBody>
                <Status status={property.status}>{property.status}</Status>
                <CardAddress>
                    {property.displayAddress}, {property.location}
                </CardAddress>
                <CardMeta>
                    {beds}
                    {property.type}
                </CardMeta>
                <CardPrice>{formatPrice(property.price, isLetting ? property.priceType : "")}</CardPrice>
            </CardBody>
        </Card>
    );
}

export default function Locality() {
    const { area: slug } = useParams();
    const area = findArea(slug);

    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const axiosInstance = axios.create({ baseURL: process.env.REACT_APP_API_URL });

    useEffect(() => {
        if (!area) {
            setIsLoading(false);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            try {
                const res = await axiosInstance.get("/properties", {
                    params: { location: area.locations.join(",") },
                });
                if (!cancelled) setProperties(Array.isArray(res.data) ? res.data : []);
            } catch (e) {
                if (!cancelled) setProperties([]);
            }
            if (!cancelled) setIsLoading(false);
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const stats = areaStats(properties);
    useSeo(localitySeo(area, stats));
    useJsonLd(localitySchema(area, stats));

    // An unknown area slug is a 404, not a thin page of its own. NotFound
    // carries the noindex and already lists every area we do cover.
    if (!area) return <NotFound />;

    const live = properties.filter((p) => p.status === "Available" || p.status === "Under Offer");
    const past = properties.filter((p) => p.status !== "Available" && p.status !== "Under Offer");
    const service = stats.lettingsLed ? "Letting" : "Estate";

    // Everything in this sentence is counted from the records themselves.
    const lede = stats.total
        ? `We have let or sold ${stats.total} ${
              stats.total === 1 ? "property" : "properties"
          } in ${area.name}${
              stats.minBeds !== null && stats.maxBeds !== null && stats.maxBeds > stats.minBeds
                  ? `, from ${stats.minBeds === 0 ? "studios" : stats.minBeds + " bedroom homes"} to ${
                        stats.maxBeds
                    } bedroom homes`
                  : ""
          }.${stats.live ? ` ${stats.live} ${stats.live === 1 ? "is" : "are"} available right now.` : ""}`
        : `Property People cover ${area.name} for lettings, sales and property management.`;

    return (
        <>
            <NavBar location="public" />

            <Header>
                <Container>
                    <Title>
                        {service} Agents in {area.name}
                    </Title>
                    <Lede>{lede}</Lede>
                </Container>
            </Header>

            <Container>
                {/* Optional hand-written local copy, and an optional landmark
                    photo beside it - see front/src/data/areaPages.js */}
                {area.intro ? (
                    area.landmarkImage ? (
                        <IntroRow>
                            <LocalCopy>{area.intro}</LocalCopy>
                            <LandmarkFigure>
                                <LandmarkImage src={area.landmarkImage} alt={area.landmarkImageAlt || area.name} />
                                {area.landmarkImageCredit && (
                                    <LandmarkCredit>{area.landmarkImageCredit}</LandmarkCredit>
                                )}
                            </LandmarkFigure>
                        </IntroRow>
                    ) : (
                        <LocalCopy style={{ marginBottom: "2rem" }}>{area.intro}</LocalCopy>
                    )
                ) : null}

                {isLoading ? (
                    <LoaderWrapper>
                        <Loader type="Puff" color="#A2DEF9" height={60} width={60} />
                    </LoaderWrapper>
                ) : (
                    <>
                        {stats.total > 0 && (
                            <StatRow>
                                <Stat>
                                    <StatValue>{stats.total}</StatValue>
                                    <StatLabel>Properties handled</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.lettings}</StatValue>
                                    <StatLabel>Lettings</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.sales}</StatValue>
                                    <StatLabel>Sales</StatLabel>
                                </Stat>
                                <Stat>
                                    <StatValue>{stats.live}</StatValue>
                                    <StatLabel>Available now</StatLabel>
                                </Stat>
                            </StatRow>
                        )}

                        {live.length > 0 && (
                            <>
                                <SectionTitle>Available now in {area.name}</SectionTitle>
                                <CardGrid>
                                    {live.map((p) => (
                                        <PropertyCard key={p._id} property={p} />
                                    ))}
                                </CardGrid>
                            </>
                        )}

                        {past.length > 0 && (
                            <>
                                <SectionTitle>
                                    Recently let and sold in {area.name}
                                </SectionTitle>
                                <CardGrid>
                                    {past.slice(0, 12).map((p) => (
                                        <PropertyCard key={p._id} property={p} />
                                    ))}
                                </CardGrid>
                            </>
                        )}

                        {stats.total === 0 && (
                            <p>
                                We don't have {area.name} listings on the site at the moment. Get in touch
                                and we'll let you know as soon as something comes up.
                            </p>
                        )}
                    </>
                )}

                {area.guidePostId && (
                    <GuideBox>
                        <SectionTitle style={{ marginTop: 0 }}>Our {area.name} area guide</SectionTitle>
                        <p>
                            We've written a guide to living in {area.name} - what the area is like, and what
                            to know before you move.
                        </p>
                        <Link to={`/blog/${area.guidePostId}`}>
                            <Button primary type="button">
                                Read the {area.name} guide
                            </Button>
                        </Link>
                    </GuideBox>
                )}

                <Cta>
                    <SectionTitle>Thinking of letting or selling in {area.name}?</SectionTitle>
                    <p>Call 020 8946 7171 or arrange a free, no-obligation valuation.</p>
                    <Link to="/contact">
                        <Button bright type="button">
                            Contact our team
                        </Button>
                    </Link>
                </Cta>

                <AreaLinks>
                    <strong>Other areas we cover:</strong>
                    <br />
                    {AREAS.filter((a) => a.slug !== area.slug).map((a) => (
                        <Link key={a.slug} to={`/estate-agents/${a.slug}`}>
                            {a.name}
                        </Link>
                    ))}
                </AreaLinks>
            </Container>

            <Footer />
        </>
    );
}
